import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import ServiceCard from '../components/services/ServiceCard';

const PRODUCT_CATEGORIES = ['All', 'Apparel', 'Accessories', 'Equipment', 'Limited'];
const SERVICE_CATEGORIES = [
  'All',
  'Body Massages',
  'Body Scrubs',
  'Body Masques',
  'Face Refreshers',
  'Foot Therapies',
  'Head Massages',
];

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('tab') === 'services' ? 'services' : 'products';
  const [tab, setTab] = useState(initialTab);

  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activePartnerId, setActivePartnerId] = useState('All');

  // Reset filters when toggling tabs
  useEffect(() => {
    setActiveCategory('All');
    setSearchQuery('');
    setActivePartnerId('All');
    setSearchParams({ tab }, { replace: true });
  }, [tab]);  // eslint-disable-line

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, s, pr] = await Promise.all([
          axiosInstance.get('/products'),
          axiosInstance.get('/services'),
          axiosInstance.get('/partners'),
        ]);
        setProducts(p.data);
        setServices(s.data);
        setPartners(pr.data);
      } catch (e) {
        toast.error('Failed to load shop');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredProducts = useMemo(
    () =>
      products
        .filter((p) => (activeCategory === 'All' ? true : p.category === activeCategory))
        .filter((p) => {
          if (!normalizedQuery) return true;
          return `${p.name} ${p.brand}`.toLowerCase().includes(normalizedQuery);
        }),
    [products, activeCategory, normalizedQuery],
  );

  const filteredServices = useMemo(
    () =>
      services
        .filter((s) =>
          activeCategory === 'All' ? true : s.service_category === activeCategory,
        )
        .filter((s) => (activePartnerId === 'All' ? true : s.partner_id === activePartnerId))
        .filter((s) => {
          if (!normalizedQuery) return true;
          return `${s.name} ${s.partner_brand || ''}`.toLowerCase().includes(normalizedQuery);
        }),
    [services, activeCategory, normalizedQuery, activePartnerId],
  );

  const categories = tab === 'services' ? SERVICE_CATEGORIES : PRODUCT_CATEGORIES;
  const showCartButton = tab === 'products';

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] pb-28 text-[#2B3A67]"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <header className="sticky top-0 z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-gray-100 pt-safe">
        <div className="flex items-center justify-between px-6 py-5">
          <div>
            <p
              className="text-xs tracking-[0.35em] uppercase text-gray-400 font-semibold"
              data-testid="shop-eyebrow"
            >
              Backyard
            </p>
            <h1 className="text-3xl font-extrabold text-[#2B3A67]" data-testid="shop-title">
              {tab === 'services' ? 'Services' : 'Market'}
            </h1>
            <p className="text-sm text-gray-500" data-testid="shop-subtitle">
              {tab === 'services'
                ? 'Curated wellness from our partners.'
                : 'Curated gear for the community.'}
            </p>
          </div>
          {showCartButton && (
            <button
              onClick={() => navigate('/cart')}
              className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm"
              data-testid="cart-button"
            >
              <i className="ph-bold ph-shopping-cart text-xl text-gray-700" />
            </button>
          )}
        </div>

        {/* Tab toggle */}
        <div className="px-6 pb-3" data-testid="shop-tab-toggle">
          <div className="bg-white rounded-2xl border border-gray-100 p-1 flex">
            <button
              data-testid="shop-tab-products"
              onClick={() => setTab('products')}
              className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-colors ${
                tab === 'products' ? 'bg-[#2B3A67] text-white' : 'text-gray-500'
              }`}
            >
              Shop
            </button>
            <button
              data-testid="shop-tab-services"
              onClick={() => setTab('services')}
              className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-colors ${
                tab === 'services' ? 'bg-[#2B3A67] text-white' : 'text-gray-500'
              }`}
            >
              Services
            </button>
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-4 flex items-center gap-3">
            <i className="ph-bold ph-magnifying-glass text-lg text-gray-400" />
            <input
              data-testid="shop-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                tab === 'services' ? 'Search services or partners' : 'Search products or brands'
              }
              className="flex-1 text-sm focus:outline-none text-[#2B3A67]"
            />
            {searchQuery && (
              <button
                data-testid="shop-search-clear"
                onClick={() => setSearchQuery('')}
                className="text-xs font-bold text-[#E54562]"
              >
                Clear
              </button>
            )}
          </div>

          <div
            className="flex gap-2 overflow-x-auto no-scrollbar"
            data-testid="shop-category-tabs"
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#2B3A67] text-white'
                    : 'bg-white border border-gray-100'
                }`}
                data-testid={`shop-category-${cat.toLowerCase().replace(/ /g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Partner pill row — services only */}
          {tab === 'services' && partners.length > 0 && (
            <div
              className="flex gap-2 overflow-x-auto no-scrollbar mt-3"
              data-testid="shop-partner-tabs"
            >
              <button
                onClick={() => setActivePartnerId('All')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap ${
                  activePartnerId === 'All'
                    ? 'bg-[#E54562] text-white'
                    : 'bg-white border border-gray-100 text-gray-500'
                }`}
                data-testid="shop-partner-all"
              >
                All Partners
              </button>
              {partners.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActivePartnerId(p.id)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap ${
                    activePartnerId === p.id
                      ? 'bg-[#E54562] text-white'
                      : 'bg-white border border-gray-100 text-gray-500'
                  }`}
                  data-testid={`shop-partner-${p.slug}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="px-6 pt-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562] mx-auto" />
            <p data-testid="shop-loading" className="mt-4 text-gray-400">
              Loading...
            </p>
          </div>
        ) : tab === 'services' ? (
          filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <p data-testid="shop-empty" className="text-gray-400">
                No services available for this filter.
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
              data-testid="shop-services-grid"
            >
              {filteredServices.map((s, idx) => (
                <ServiceCard key={s.id} service={s} index={idx} />
              ))}
            </div>
          )
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p data-testid="shop-empty" className="text-gray-400">
              No products available right now.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-4"
            data-testid="shop-products-grid"
          >
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                data-testid={`product-card-${index}`}
                className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100"
              >
                <div className="relative">
                  <img
                    data-testid={`product-image-${product.id}`}
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-36 object-cover"
                  />
                  {product.discounted_price && (
                    <span
                      data-testid={`product-sale-${product.id}`}
                      className="absolute top-2 left-2 bg-[#F4D341] text-[#2B3A67] text-[10px] font-black px-2 py-1 rounded-full"
                    >
                      SALE
                    </span>
                  )}
                </div>

                <div className="p-4">
                  <p
                    data-testid={`product-brand-${product.id}`}
                    className="text-[10px] font-bold text-gray-400 uppercase tracking-widest"
                  >
                    {product.brand}
                  </p>
                  <h3
                    data-testid={`product-name-${product.id}`}
                    className="font-bold text-sm mt-1 mb-2 line-clamp-2"
                  >
                    {product.name}
                  </h3>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {product.discounted_price ? (
                        <>
                          <span
                            data-testid={`product-price-${product.id}`}
                            className="font-black text-[#E54562]"
                          >
                            {formatCurrency(product.discounted_price)}
                          </span>
                          <span
                            data-testid={`product-base-price-${product.id}`}
                            className="text-xs text-gray-400 line-through"
                          >
                            {formatCurrency(product.base_price)}
                          </span>
                        </>
                      ) : (
                        <span
                          data-testid={`product-price-${product.id}`}
                          className="font-black text-[#E54562]"
                        >
                          {formatCurrency(product.base_price)}
                        </span>
                      )}
                    </div>
                    <button
                      className="w-9 h-9 bg-[#2B3A67] text-white rounded-full flex items-center justify-center"
                      onClick={() => navigate(`/shop/${product.id}`)}
                      data-testid={`product-add-${product.id}`}
                    >
                      <i className="ph-bold ph-plus" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
